/**
 * Project board page — wires together Board / ListView / Drawer / SprintPicker.
 *
 * URL shape:
 *   /w/:wsId/p/:pKey                   → board view, all tasks
 *   /w/:wsId/p/:pKey/t/:tKey           → board view, drawer opens on tKey
 *   /w/:wsId/p/:pKey/sprint/:sprintKey → board view, sprint scope
 *
 * View switcher (Kanban / List) is local state. ?view=list in the URL could
 * be added later for shareable deep links.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link, Outlet, useMatchRoute } from '@tanstack/react-router'
import {
  useBoard,
  useCreateTask,
  useMoveTask,
  useProjectByKey,
} from '@/features/projects/board'
import { useProjectShortcuts } from '@/features/projects/keyboard'
import { useBoardSubscription } from '@/features/realtime/useBoardSubscription'
import { useResolveProjectKey } from '@/features/transfers/transfers'
import {
  BOARD_SORT_OPTIONS,
  DEFAULT_BOARD_SORT,
  type BoardSort,
} from '@/features/projects/sorting'
import { MobileBoard } from '@/components/kanban/MobileBoard'
import { Board } from '@/components/kanban/Board'
import { ListView } from '@/components/list/ListView'
import { TaskDrawer } from '@/components/task/Drawer'
import { SprintPicker } from '@/components/sprint/SprintPicker'
import { SortSelect } from '@/components/ui/SortSelect'
import { cn } from '@/lib/utils'
import type { Task } from '@friday/shared/realtime'

type View = 'kanban' | 'list'
type SprintScope = string | null | 'all'

export function ProjectBoard() {
  const { wsId, pKey, tKey } = useParams({ strict: false }) as {
    wsId: string
    pKey: string
    tKey?: string
  }
  const navigate = useNavigate()
  const [view, setView] = useState<View>('kanban')
  const [sprintScope, setSprintScope] = useState<SprintScope>('all')
  // FRIDAY-75: the board sort lives here, next to the view switcher, so the
  // board keeps its full height (the columns are drag targets).
  const [boardSort, setBoardSort] = useState<BoardSort>(DEFAULT_BOARD_SORT)

  // Resolve project by key.
  const projectQ = useProjectByKey(wsId, pKey)
  const project = projectQ.data

  // Once the project is loaded, fetch the board.
  const board = useBoard(wsId, project?.id)

  // The drawer task id is derived from the URL param tKey, with overrides
  // when the user clicks a card. We keep it as local state so clicking
  // different cards updates the URL via navigate({ replace: true }).
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null)

  const create = useCreateTask(wsId, project?.id, board.data?.columns[0]?.id)
  const move = useMoveTask(wsId, project?.id)
  // Old links: a board that left this workspace resolves to its new home
  // only when the caller can open it there.
  const missing = projectQ.isSuccess && projectQ.data === null
  const home = useResolveProjectKey(pKey, missing)
  useEffect(() => {
    if (home.data && home.data.workspaceId !== wsId) {
      navigate({ to: '/w/$wsId/p/$pKey', params: { wsId: home.data.workspaceId, pKey: home.data.key }, replace: true })
    }
  }, [home.data, wsId, navigate])

  // Detect "full-page" sub-routes (milestones, members, settings) that take
  // over the whole main area. The task/sprint sub-routes are URL-only and
  // still show the board + drawer, so we don't need to swap them out.
  const matchRoute = useMatchRoute()
  const onSubPage = !!(
    matchRoute({ to: '/w/$wsId/p/$pKey/milestones', params: { wsId, pKey } }) ||
    matchRoute({ to: '/w/$wsId/p/$pKey/members', params: { wsId, pKey } }) ||
    matchRoute({ to: '/w/$wsId/p/$pKey/settings', params: { wsId, pKey } }) ||
    matchRoute({ to: '/w/$wsId/p/$pKey/settings/members', params: { wsId, pKey } }) ||
    matchRoute({ to: '/w/$wsId/p/$pKey/settings/labels', params: { wsId, pKey } }) ||
    matchRoute({ to: '/w/$wsId/p/$pKey/settings/milestones', params: { wsId, pKey } })
  )

  // Realtime: subscribe to this project + workspace and refetch on any event.
  useBoardSubscription({ wsId, pId: project?.id })

  useEffect(() => {
    if (!project) return
    if (!tKey) {
      setOpenTaskId(null)
      return
    }
    const t = board.data?.tasks.find((x) => x.key === tKey)
    if (t) setOpenTaskId(t.id)
  }, [tKey, board.data, project])

  useProjectShortcuts({
    columns: board.data?.columns ?? [],
    tasks: board.data?.tasks ?? [],
    focusedTaskId,
    setFocusedTaskId,
    openTask: (t) => openTask(t),
    moveTask: ({ taskId, columnId, version }) => {
      move.mutate({ taskId, columnId, prevTaskId: null, nextTaskId: null, version })
    },
    createTask: (title, columnId) => {
      // useCreateTask announces the new task with an Open action.
      create.mutate({ title, priority: 'medium' })
    },
  })

  function openTask(t: Task) {
    setOpenTaskId(t.id)
    navigate({
      to: '/w/$wsId/p/$pKey/t/$tKey',
      params: { wsId, pKey, tKey: t.key },
      replace: true,
    })
  }

  function closeTask() {
    setOpenTaskId(null)
    navigate({ to: '/w/$wsId/p/$pKey', params: { wsId, pKey }, replace: true })
  }

  if (missing && (home.isError || (home.data && home.data.workspaceId === wsId))) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">Project not found.</div>
    )
  }

  if (projectQ.isLoading || !project) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">Loading project…</div>
    )
  }

  if (projectQ.error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">Project not found.</div>
    )
  }

  // Milestones / members are full-page sub-routes with their own header.
  // Render the child route directly without the project header / board.
  if (onSubPage) {
    return <Outlet />
  }

  return (
    <div className="project-board flex h-full min-h-0 flex-col">
      <header className="project-header relative z-20 flex flex-wrap items-center gap-2 border-b border-surface-200 bg-white/60 px-4 py-2 backdrop-blur dark:border-surface-700 dark:bg-surface-900/60 md:pr-14">
        <BoardHeaderIdentity project={project} />

        <div className="ml-3">
          <SprintPicker wsId={wsId} pId={project.id} value={sprintScope} onChange={setSprintScope} />
        </div>

        <ProjectNavLinks wsId={wsId} pKey={pKey} />

        <div className="ml-auto flex items-center gap-2">
          {view === 'kanban' && (
            <SortSelect
              value={boardSort.key}
              options={BOARD_SORT_OPTIONS}
              direction={boardSort.direction}
              onChange={(key) =>
                setBoardSort((current) => ({
                  ...current,
                  key: key as BoardSort['key'],
                }))
              }
              onDirectionChange={(direction) =>
                setBoardSort((current) => ({ ...current, direction }))
              }
            />
          )}
          <div className="hidden md:block">
            <ViewSwitcher view={view} onChange={setView} />
          </div>
        </div>
      </header>

      <div className="hidden min-h-0 flex-1 overflow-hidden md:block">
        {board.isLoading ? (
          <div className="text-surface-500 flex h-full items-center justify-center text-sm">
            Loading board…
          </div>
        ) : board.error ? (
          <div className="flex h-full items-center justify-center text-sm text-red-500">
            Failed to load board.
          </div>
        ) : view === 'kanban' ? (
          <Board
            wsId={wsId}
            pId={project.id}
            filterSprintId={sprintScope === 'all' || sprintScope == null ? null : sprintScope}
            onOpenTask={openTask}
            selectedTaskId={openTaskId}
            sort={boardSort}
          />
        ) : (
          <ListView
            wsId={wsId}
            pId={project.id}
            filterSprintId={sprintScope === 'all' ? null : sprintScope}
            onOpenTask={openTask}
          />
        )}
      </div>

      <div className="min-h-0 flex-1 md:hidden">
        {board.isLoading ? (
          <p className="p-4" role="status">
            Loading board…
          </p>
        ) : board.error ? (
          <p className="p-4" role="alert">
            Failed to load board.{' '}
            <button className="btn-outline" onClick={() => board.refetch()}>
              Retry
            </button>
          </p>
        ) : (
          <MobileBoard
            key={project.id}
            wsId={wsId}
            pId={project.id}
            sprintScope={sprintScope}
            onOpenTask={openTask}
            sort={boardSort}
          />
        )}
      </div>

      <TaskDrawer wsId={wsId} pId={project.id} taskId={openTaskId} onClose={closeTask} />
    </div>
  )
}

function ViewSwitcher({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="border-surface-300 dark:border-surface-700 flex items-center overflow-hidden rounded-md border text-xs">
      <button
        onClick={() => onChange('kanban')}
        className={cn(
          'px-2.5 py-1',
          view === 'kanban'
            ? 'bg-brand-500 text-white'
            : 'text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800',
        )}
      >
        Kanban
      </button>
      <button
        onClick={() => onChange('list')}
        className={cn(
          'border-surface-300 dark:border-surface-700 border-l px-2.5 py-1',
          view === 'list'
            ? 'bg-brand-500 text-white'
            : 'text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800',
        )}
      >
        List
      </button>
    </div>
  )
}

/**
 * Board name, colour and icon as the header shows them. Exported so the
 * settings browser test can prove an identity save reaches the header
 * without a reload.
 */
export function BoardHeaderIdentity({
  project,
}: {
  project: { key: string; name: string; color: string; icon?: string | null }
}) {
  return (
    <div className="flex items-center gap-2" data-testid="board-header-identity">
      <span
        className="flex h-6 w-6 items-center justify-center rounded font-mono text-[10px] text-white"
        style={{ backgroundColor: project.color }}
      >
        {project.key.slice(0, 2)}
      </span>
      {project.icon && (
        <span className="text-sm" aria-hidden="true">
          {project.icon}
        </span>
      )}
      <h1 className="text-sm font-semibold">{project.name}</h1>
      <span className="text-surface-500 font-mono text-[11px]">{project.key}</span>
    </div>
  )
}

/**
 * Board sub-page links. Exported so the responsive header can be exercised
 * in a browser test without booting the whole board.
 */
/**
 * The board's own settings. Members, labels and milestones live in the
 * workspace settings; the board keeps one entry that opens its own
 * settings, where the board's members, labels and milestones are shown.
 */
export function ProjectNavLinks({ wsId, pKey }: { wsId: string; pKey: string }) {
  return (
    <div className="ml-3 flex flex-wrap items-center gap-1 text-xs">
      <NavLink to="/w/$wsId/p/$pKey/settings" params={{ wsId, pKey }} label="Settings" />
    </div>
  )
}

function NavLink<TParams extends Record<string, string>>({
  to,
  params,
  label,
}: {
  to: '/w/$wsId/p/$pKey/settings'
  params: TParams
  label: string
}) {
  return (
    <Link
      to={to}
      params={params as never}
      className="border-surface-300 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 hover:border-brand-500 rounded-md border px-2 py-1"
    >
      {label}
    </Link>
  )
}
